import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { AlertCircle, ExternalLink, NotebookPen, Pencil } from "lucide-react";
import { useState } from "react";

import { AppShell } from "components/AppShell";
import { AvatarPlaceholder } from "components/AvatarPlaceholder";
import { EmptyState, LoadingCards } from "components/states";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card } from "components/ui/card";
import { cn } from "utils";

import { PersonNoteEditor } from "./PersonNoteEditor";
import type { EventStatus, NoteGroup, NoteView } from "./notes-model";
import { useMyNotes } from "./use-my-notes";

// The Button's `secondary` variant is a gradient of --hb-blue, which Dusk
// lightens, so the second emphasised button is spelled out from the edge tokens.
// Light drops the edge: the hard outline belongs to the main button only.
const RAISED_BUTTON_CLASS =
  "border border-transparent bg-dusk-tile-a text-white hover:bg-hb-blue-fill hover:brightness-110 dark:border-edge dark:border-lit-edge dark:shadow-lit dark:hover:shadow-lit-strong";

export function MyNotesPage({
  uid,
  email,
  signOut,
}: {
  uid: string;
  email: string | null;
  signOut: () => void;
}) {
  const [refreshKey, setRefreshKey] = useState(0);
  const state = useMyNotes(uid, refreshKey);

  return (
    <AppShell identity={{ email }} onSignOut={signOut} title="My notes">
      <p className="mb-6 max-w-2xl text-sm text-muted-foreground">
        Private notes you wrote on attendees&apos; profiles. Only you can see
        them, and they stay here after the event ends.
      </p>

      {state.status === "loading" && <LoadingCards cards={3} />}

      {state.status === "error" && (
        <EmptyState
          icon={AlertCircle}
          title="Could not load your notes"
          testId="my-notes-error"
        >
          {state.error}
        </EmptyState>
      )}

      {state.status === "success" && state.groups.length === 0 && (
        <EmptyState
          icon={NotebookPen}
          title="No notes yet"
          testId="my-notes-empty"
        >
          Open somebody&apos;s profile in an event&apos;s attendee list and
          write a note there. It shows up here.
        </EmptyState>
      )}

      {state.status === "success" && state.groups.length > 0 && (
        <div className="space-y-8" data-testid="my-notes">
          {state.groups.map((group) => (
            <EventNotes
              key={group.eventId}
              uid={uid}
              group={group}
              onEdited={() => setRefreshKey((key) => key + 1)}
            />
          ))}
        </div>
      )}
    </AppShell>
  );
}

function EventNotes({
  uid,
  group,
  onEdited,
}: {
  uid: string;
  group: NoteGroup;
  onEdited: () => void;
}) {
  const openable =
    group.slug &&
    (group.status.kind === "member" || group.status.kind === "not-registered");
  return (
    <section
      className="space-y-3"
      aria-labelledby={`notes-event-${group.eventId}`}
      data-testid={`my-notes-event-${group.eventId}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2
          id={`notes-event-${group.eventId}`}
          className="min-w-0 text-lg font-semibold text-foreground wrap-anywhere"
        >
          {group.title}
        </h2>
        <StatusBadge status={group.status} />
        {openable && (
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="max-sm:h-11 sm:ml-auto"
          >
            <Link to="/e/$slug" params={{ slug: group.slug ?? "" }}>
              Open event
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {group.notes.map((view) => (
          <NoteCard
            key={view.note.id}
            uid={uid}
            view={view}
            onEdited={onEdited}
          />
        ))}
      </div>
    </section>
  );
}

function StatusBadge({ status }: { status: EventStatus }) {
  switch (status.kind) {
    case "deleted":
      return (
        <Badge variant="neutral" data-testid="my-notes-event-deleted">
          Event deleted
        </Badge>
      );
    case "not-registered":
      return <Badge variant="outline">You left this event</Badge>;
    case "unpublished":
      return <Badge variant="outline">Not published</Badge>;
    default:
      return null;
  }
}

function NoteCard({
  uid,
  view,
  onEdited,
}: {
  uid: string;
  view: NoteView;
  onEdited: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const { note } = view;
  const edited = note.updatedAt?.toDate();

  return (
    <Card
      className="min-w-0 space-y-3 border-border p-4"
      data-testid={`my-note-${note.attendeeId}`}
    >
      <div className="flex items-center gap-3">
        {view.avatar ? (
          <img
            src={view.avatar}
            alt=""
            className="h-10 w-10 shrink-0 rounded-full object-cover"
          />
        ) : (
          <AvatarPlaceholder name={view.name} size="sm" />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-medium text-foreground" data-testid="my-note-name">
            {view.name}
          </p>
          <p className="text-xs text-muted-foreground">
            {view.inDirectory === false
              ? "No longer on the attendee list"
              : edited
                ? `Edited ${format(edited, "d MMM yyyy")}`
                : null}
          </p>
        </div>
        {!editing && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 max-sm:h-11"
            onClick={() => setEditing(true)}
            aria-label={`Edit note about ${view.name}`}
            data-testid="my-note-edit"
          >
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <PersonNoteEditor
            autoFocus
            target={{
              uid,
              eventId: note.eventId,
              attendeeId: note.attendeeId,
              // Kept as stored. Refreshing it from the directory here would
              // copy a newer name the author may never have seen on screen.
              personName: note.personName,
              eventTitle: note.eventTitle,
              eventSlug: note.eventSlug,
            }}
          />
          <Button
            type="button"
            size="sm"
            className={cn("max-sm:h-11", RAISED_BUTTON_CLASS)}
            onClick={() => {
              setEditing(false);
              onEdited();
            }}
            data-testid="my-note-done"
          >
            Done
          </Button>
        </div>
      ) : (
        <p
          className="whitespace-pre-line text-sm leading-relaxed text-foreground"
          data-testid="my-note-body"
        >
          {note.body}
        </p>
      )}
    </Card>
  );
}
